/**
 * ProductMiceDataService
 * 
 * M.I.C.E hizmet çözümü verilerini parse eden ve transform eden utility class
 */

import { transformMediaUrl } from '@/lib/media-url-transformer';

export interface Service {
    title?: string;
    name?: string;
    value?: string;
    text?: string;
    description?: string;
    desc?: string;
    detail?: string;
}

export interface Reference {
    year: number | null;
    subject: string;
    description: string | null;
    media: {
        id: number;
        url: string;
        type: 'image' | 'video';
        thumbnail?: string;
    } | null;
}

/** Overview sağ kolon: istatistik / vurgu kartları (repeater) */
export interface OverviewStatItem {
    value: string;
    label: string;
    description: string | null;
    style: 'stat' | 'highlight';
    icon: string | null;
}

export interface OverviewHighlight {
    title: string | null;
    textHtml: string | null;
    badge: string | null;
    ctaText: string | null;
    ctaUrl: string | null;
}

export class ProductMiceDataService {
    /**
     * Hizmet tipini parse et
     */
    static parseServiceType(metas: any): string | null {
        if (!metas?.service_type) return null;
        return String(metas.service_type);
    }

    /**
     * Lokasyonu parse et (tagify array)
     */
    static parseLocation(metas: any): string[] {
        if (!metas?.location) return [];
        
        // Eğer string ise JSON parse et
        if (typeof metas.location === 'string') {
            try {
                const parsed = JSON.parse(metas.location);
                if (Array.isArray(parsed)) {
                    return parsed.map(loc => String(loc)).filter(Boolean);
                }
                return [String(parsed)].filter(Boolean);
            } catch {
                // JSON değilse direkt string olarak kullan
                return [metas.location].filter(Boolean);
            }
        }
        
        // Eğer array ise direkt kullan
        if (Array.isArray(metas.location)) {
            return metas.location.map((loc: any) => String(loc)).filter(Boolean);
        }
        
        return [];
    }

    /**
     * Hizmet özelliklerini parse et (services_repeater)
     */
    static parseServiceFeatures(metas: any): { included: Service[] } {
        const included: Service[] = [];

        // Helper function to parse and normalize services data
        const parseServicesData = (data: any): any[] => {
            if (!data) return [];
            
            // If it's a JSON string, parse it
            if (typeof data === 'string') {
                try {
                    const parsed = JSON.parse(data);
                    return Array.isArray(parsed) ? parsed : [];
                } catch {
                    // If parsing fails, treat as single string
                    return [data];
                }
            }
            
            // If it's already an array, return it
            if (Array.isArray(data)) {
                return data;
            }
            
            // If it's an object, wrap it in an array
            if (typeof data === 'object' && data !== null) {
                return [data];
            }
            
            return [];
        };

        // Parse and process included services
        const includedData = parseServicesData(metas?.service_features);
        includedData.forEach((service: any) => {
            const extracted = ProductMiceDataService.extractServicesRepeaterRow(service);
            if (extracted) {
                included.push(extracted);
            }
        });

        return { included };
    }

    /**
     * services_repeater tek satırı (hizmet özellikleri + süreç adımları ortak)
     */
    static extractServicesRepeaterRow(service: any): Service | null {
        if (service === null || service === undefined) {
            return null;
        }

        if (typeof service === 'string') {
            const trimmed = service.trim();
            return trimmed ? { title: trimmed, description: '' } : null;
        }

        if (typeof service === 'object' && service !== null) {
            let title = '';
            if (service.title !== undefined && service.title !== null) {
                title = String(service.title);
            } else if (service.name !== undefined && service.name !== null) {
                title = String(service.name);
            } else if (service.value !== undefined && service.value !== null) {
                title = String(service.value);
            } else if (service.text !== undefined && service.text !== null) {
                title = String(service.text);
            } else if (service.label !== undefined && service.label !== null) {
                title = String(service.label);
            } else if (service.service !== undefined && service.service !== null) {
                title = String(service.service);
            }

            const description =
                service.description !== undefined && service.description !== null
                    ? String(service.description)
                    : service.desc !== undefined && service.desc !== null
                      ? String(service.desc)
                      : service.detail !== undefined && service.detail !== null
                        ? String(service.detail)
                        : '';

            const trimmedTitle = title.trim();
            if (trimmedTitle) {
                return {
                    title: trimmedTitle,
                    description: description.trim(),
                };
            }
        }

        return null;
    }

    /**
     * process alanı: services_repeater (dizi / JSON dizi string).
     * Eski ürünlerde tek parça HTML string ise timeline için [] döner.
     */
    static parseProcessSteps(metas: any): Array<{ title: string; description: string }> {
        const raw = metas?.process;
        const rows = ProductMiceDataService.normalizeJsonRepeaterRaw(raw);
        const out: Array<{ title: string; description: string }> = [];
        for (const row of rows) {
            const s = ProductMiceDataService.extractServicesRepeaterRow(row);
            if (s?.title?.trim()) {
                out.push({ title: s.title.trim(), description: (s.description || '').trim() });
            }
        }
        return out;
    }

    /**
     * services_repeater kaydı: JSON dizi string veya dizi. Eski tek parça HTML string ise [].
     */
    private static normalizeJsonRepeaterRaw(raw: any): any[] {
        if (raw === null || raw === undefined) return [];
        if (typeof raw === 'string') {
            const t = raw.trim();
            if (!t) return [];
            if (!t.startsWith('[')) {
                return [];
            }
            try {
                const parsed = JSON.parse(t);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'object') return [raw];
        return [];
    }

    /**
     * Neden Biz — services_repeater (Hizmet Özellikleri ile aynı şema).
     * Eski rich_text HTML ise [] (içerik yeniden girilmeli).
     */
    static parseWhyUsItems(metas: any): Service[] {
        const raw = metas?.why_us;
        const rows = ProductMiceDataService.normalizeJsonRepeaterRaw(raw);
        const out: Service[] = [];
        for (const row of rows) {
            const s = ProductMiceDataService.extractServicesRepeaterRow(row);
            if (s) {
                out.push(s);
            }
        }
        return out;
    }

    /**
     * Süreç bölümü giriş metni (rich_text), timeline dışında
     */
    static parseProcessDesc(metas: any, fields: any): string | null {
        const v = metas?.process_desc ?? fields?.process_desc?.field_value ?? null;
        return v != null && String(v).trim() !== '' ? String(v) : null;
    }

    /**
     * overview_stats repeater (JSON string veya dizi)
     */
    static parseOverviewStats(metas: any): OverviewStatItem[] {
        const raw = metas?.overview_stats;
        let rows: any[] = [];
        if (raw == null) return [];
        if (typeof raw === 'string') {
            try {
                const parsed = JSON.parse(raw);
                rows = Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        } else if (Array.isArray(raw)) {
            rows = raw;
        } else {
            return [];
        }

        const out: OverviewStatItem[] = [];
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            const value = row.value != null ? String(row.value).trim() : '';
            const label = row.label != null ? String(row.label).trim() : '';
            if (!value || !label) continue;
            const description =
                row.description != null && String(row.description).trim() !== ''
                    ? String(row.description).trim()
                    : null;
            const rawStyle = row.style != null ? String(row.style).trim().toLowerCase() : '';
            const style: 'stat' | 'highlight' = rawStyle === 'highlight' ? 'highlight' : 'stat';
            const icon = row.icon != null && String(row.icon).trim() !== '' ? String(row.icon).trim() : null;
            out.push({ value, label, description, style, icon });
        }
        return out;
    }

    /**
     * Overview sağ kolon vurgu kutusu (metin alanları)
     */
    static parseOverviewHighlight(metas: any): OverviewHighlight {
        const titleRaw = metas?.overview_highlight_title;
        const title =
            titleRaw != null && String(titleRaw).trim() !== '' ? String(titleRaw).trim() : null;
        const textRaw = metas?.overview_highlight_text;
        const textHtml =
            textRaw != null && String(textRaw).trim() !== '' ? String(textRaw) : null;
        const badgeRaw = metas?.overview_highlight_badge;
        const badge =
            badgeRaw != null && String(badgeRaw).trim() !== '' ? String(badgeRaw).trim() : null;
        const ctaTextRaw = metas?.overview_highlight_cta_text;
        const ctaText =
            ctaTextRaw != null && String(ctaTextRaw).trim() !== '' ? String(ctaTextRaw).trim() : null;
        const ctaUrlRaw = metas?.overview_highlight_cta_url;
        const ctaUrl =
            ctaUrlRaw != null && String(ctaUrlRaw).trim() !== '' ? String(ctaUrlRaw).trim() : null;

        return {
            title,
            textHtml,
            badge,
            ctaText,
            ctaUrl,
        };
    }

    /**
     * Referansları parse et.
     * - references_expanded: API'den gelen expand edilmiş referans ürünleri (yeni format)
     * - references: Eski repeater formatı (geriye uyumluluk) veya ID listesi
     */
    static parseReferences(metas: any): Reference[] {
        // Yeni format: API references_expanded (type=reference Product'lar expand edilmiş)
        const expanded = metas?.references_expanded;
        if (Array.isArray(expanded) && expanded.length > 0) {
            return expanded
                .map((ref: any): Reference | null => {
                    if (!ref) return null;
                    const subject = ref.subject ? String(ref.subject) : (ref.name ? String(ref.name) : '');
                    if (!subject) return null;
                    const year = ref.year != null ? (typeof ref.year === 'number' ? ref.year : parseInt(String(ref.year), 10)) : null;
                    const description = ref.description ? String(ref.description) : null;
                    let media: Reference['media'] = null;
                    if (ref.media && ref.media.id) {
                        const url = (ref.media.url ? transformMediaUrl(ref.media.url) : '').trim();
                        if (url) {
                            const mediaType = (ref.media.type === 'video' ? 'video' : 'image') as 'image' | 'video';
                            media = {
                                id: ref.media.id,
                                url,
                                type: mediaType,
                                thumbnail: ref.media.thumbnail ? transformMediaUrl(ref.media.thumbnail) : undefined,
                            };
                        }
                    }
                    return { year, subject, description, media };
                })
                .filter((r): r is Reference => r !== null);
        }

        // Eski repeater formatı (geriye uyumluluk)
        const refs = metas?.references;
        if (!refs || !Array.isArray(refs)) return [];

        const items = refs as any[];
        // ID listesi ise (tagify'den gelen) expand edilmemiş - boş döndür (API'de expand edilmeli)
        if (items.length > 0 && (typeof items[0] === 'number' || typeof items[0] === 'string')) {
            return [];
        }

        return items
            .map((ref: any): Reference | null => {
                if (!ref) return null;
                const subject = ref.subject ? String(ref.subject) : '';
                if (!subject) return null;
                const year = ref.year != null ? (typeof ref.year === 'number' ? ref.year : parseInt(String(ref.year), 10)) : null;
                const description = ref.description ? String(ref.description) : null;
                let media: Reference['media'] = null;
                if (ref.media) {
                    const mediaId = typeof ref.media === 'number' ? ref.media : (ref.media?.id ?? ref.media?.media_id ?? null);
                    if (mediaId) {
                        const mediaType = ref.media_type === 'video' || ref.media?.type === 'video' ? 'video' : 'image';
                        const mediaUrl = (ref.media?.url ? transformMediaUrl(ref.media.url) : (ref.media?.path ? transformMediaUrl(ref.media.path) : '')).trim();
                        if (mediaUrl) {
                            media = {
                                id: mediaId,
                                url: mediaUrl,
                                type: mediaType as 'image' | 'video',
                                thumbnail: ref.media?.thumbnail ? transformMediaUrl(ref.media.thumbnail) : undefined,
                            };
                        }
                    }
                }
                return { year, subject, description, media };
            })
            .filter((r): r is Reference => r !== null);
    }

    /**
     * İçerik alanlarını parse et (rich_text fields)
     */
    static parseContentFields(metas: any, fields: any, language: string = 'en'): {
        overview: string | null;
        additionalInfo: string | null;
    } {
        const overview = metas?.overview || fields?.overview?.field_value || null;
        const additionalInfo = metas?.additional_info || fields?.additional_info?.field_value || null;

        return {
            overview: overview ? String(overview) : null,
            additionalInfo: additionalInfo ? String(additionalInfo) : null,
        };
    }

    /**
     * Bu anahtarlar parseContentFields + ProductMiceDetail'de zaten ayrı section olarak çiziliyor.
     * parseTabFields'a dahil edilirse aynı HTML iki kez (birinde çeviri başlığı, birinde slug) görünür.
     */
    private static readonly DEDICATED_SECTION_FIELD_KEYS = new Set([
        'overview',
        'overview_stats',
        'overview_highlight_title',
        'overview_highlight_text',
        'overview_highlight_badge',
        'overview_highlight_cta_text',
        'overview_highlight_cta_url',
        'why_us',
        'process',
        'process_desc',
        'additional_info',
    ]);

    /**
     * Tab field'larını parse et (rich_text field'ları tab_names ile gruplanmış)
     */
    static parseTabFields(metas: any, fields: any, language: string = 'en'): Array<{
        tabName: string;
        sectionId: string;
        fields: Array<{
            fieldKey: string;
            fieldTitle: string;
            content: string;
        }>;
    }> {
        const tabFields: Array<{
            tabName: string;
            sectionId: string;
            fields: Array<{
                fieldKey: string;
                fieldTitle: string;
                content: string;
            }>;
        }> = [];

        // Tab group'larına göre field'ları grupla
        const tabGroups = new Map<string, Array<{ fieldKey: string; fieldTitle: string; content: string }>>();
        
        Object.entries(fields || {}).forEach(([key, field]: [string, any]) => {
            if (!field || typeof field !== 'object') return;

            if (ProductMiceDataService.DEDICATED_SECTION_FIELD_KEYS.has(key)) {
                return;
            }
            
            // Rich text field'ları ve menü item'ları al
            if (field.field_type === 'rich_text' && field.is_menu_item) {
                const tabGroupKey = field.tab_group_key || field.menu_section_id || key;
                const tabSectionId = field.tab_section_id || field.menu_section_id || tabGroupKey;
                const fieldValue = metas[key] || field.field_value;
                const fieldTitle = field.field_title || field.field_titles?.[language] || key;
                const tabName = field.tab_names?.[language] || field.tab_name || tabGroupKey || 'Information';

                if (fieldValue) {
                    if (!tabGroups.has(tabGroupKey)) {
                        tabGroups.set(tabGroupKey, []);
                    }
                    
                    tabGroups.get(tabGroupKey)!.push({
                        fieldKey: key,
                        fieldTitle: String(fieldTitle),
                        content: String(fieldValue),
                    });
                }
            }
        });

        // Tab fields'ı oluştur
        tabGroups.forEach((fields, tabGroupKey) => {
            const firstField = fields[0];
            const field = Object.values(fields).find(f => f.fieldKey === tabGroupKey) || firstField;
            
            // Tab name'i field'dan al
            const fieldObj = Object.values(fields).find(f => 
                fields.find(ff => ff.fieldKey === tabGroupKey)
            );
            const tabName = fieldObj?.fieldTitle || tabGroupKey;
            const sectionId = tabGroupKey.replace(/_/g, '-');

            tabFields.push({
                tabName: String(tabName),
                sectionId,
                fields,
            });
        });

        return tabFields;
    }
}

